<template>
  <teleport to="body">
    <div v-if="open" class="modal-overlay" :class="{ fullscreen: isFullscreen }">
      <div class="modal-content" :class="{ fullscreen: isFullscreen }">
        <div class="modal-header">
          <h3>{{ title }}{{ description ? ' : ' + description : '' }}</h3>
          <div class="modal-controls">
            <a-tooltip
              :title="isFullscreen ? 'Normal Screen' : 'Full Screen'"
              :placement="isFullscreen ? 'left' : 'bottom'"
            >
              <button @click="toggleFullscreen" class="control-btn">
                {{ isFullscreen ? '–' : '⤢' }}
              </button>
            </a-tooltip>
            <a-tooltip title="Close" :placement="isFullscreen ? 'left' : 'bottom'">
              <button @click="closeModal" class="control-btn">×</button>
            </a-tooltip>
          </div>
        </div>
        <div class="modal-body">
          <slot></slot>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
  import { ref, watch, onUnmounted } from 'vue'

  const props = defineProps<{
    open: boolean
    title: string
    description?: string
  }>()

  const emit = defineEmits(['close'])

  const isFullscreen = ref(false)

  // Save/restore body state to prevent background scrolling while modal is open
  let savedBodyOverflow = ''
  let savedBodyPosition = ''
  let savedBodyTop = ''
  let savedScrollY = 0

  function preventTouchMove(e: TouchEvent) {
    e.preventDefault()
  }

  watch(
    () => props.open,
    (open) => {
      if (open) {
        // store current scroll/overflow state
        savedBodyOverflow = document.body.style.overflow || ''
        savedBodyPosition = document.body.style.position || ''
        savedBodyTop = document.body.style.top || ''
        savedScrollY = window.scrollY || window.pageYOffset || 0

        // lock background scroll and preserve visual position
        document.body.style.overflow = 'hidden'
        document.body.style.position = 'fixed'
        document.body.style.top = `-${savedScrollY}px`

        // prevent touch bounce on iOS
        document.addEventListener('touchmove', preventTouchMove, { passive: false })
      } else {
        // restore body state and scroll position
        document.body.style.overflow = savedBodyOverflow
        document.body.style.position = savedBodyPosition
        document.body.style.top = savedBodyTop

        document.removeEventListener('touchmove', preventTouchMove)
        window.scrollTo(0, savedScrollY)
      }
    }
  )

  onUnmounted(() => {
    // ensure cleanup if component is destroyed while modal open
    document.body.style.overflow = savedBodyOverflow
    document.body.style.position = savedBodyPosition
    document.body.style.top = savedBodyTop
    document.removeEventListener('touchmove', preventTouchMove)
    window.scrollTo(0, savedScrollY)
  })

  function toggleFullscreen() {
    isFullscreen.value = !isFullscreen.value
  }

  function closeModal() {
    emit('close')
  }
</script>

<style scoped>
  .error-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #ef4444;
    font-weight: 600;
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 12px;
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e5e5;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .data-grid-container {
    height: 100%;
    width: 100%;
    overflow: hidden;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-overlay.fullscreen {
    background: none;
    align-items: stretch;
    justify-content: stretch;
  }

  .modal-content {
    background: var(--background);
    border-radius: 8px;
    width: 80vw;
    max-width: 1000px;
    height: 70vh;
    max-height: 600px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .dark .modal-content {
    background: var(--card);
  }

  .modal-content.fullscreen {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    border-radius: 0;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
    background: var(--card);
  }

  .modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .modal-controls {
    display: flex;
    gap: 8px;
  }

  .control-btn {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    color: #666;
  }

  .control-btn:hover {
    background: var(--border);
    color: var(--foreground);
  }

  .modal-body {
    flex: 1;
    padding: 20px;
    overflow: auto;
  }
</style>